import { CommonModule } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Editor } from 'primeng/editor';
import { EditorSpellcheckDirective } from '../../shared/directives/editor-spellcheck.directive';
import { Toast } from 'primeng/toast';
import { ToggleSwitch } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil, filter } from 'rxjs';
import { ProjectModel, UpdateProjectSettingsRequest } from '../../models/project.model';
import { ProjectService } from '../../services/project.service';
import { ProjectUserModel } from '../../services/user.service';
import { ProjectStateService } from '../../services/project-state.service';
import { SSOService } from '../../services/sso.service';
import { UserService } from '../../services/user.service';
import { PermissionHelperService } from '../../services/permission-helper.service';
import { SignalRService } from '../../services/signalr.service';
import { ProjectfileService } from '../../services/projectfile.service';
import { PAGE_KEYS, PERMISSION_KEYS } from '../../constants/permission.constants';
import * as L from 'leaflet';

const psIconDefault = L.icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

@Component({
  selector: 'app-projectsetting',
  imports: [CommonModule, FormsModule, Toast, ConfirmDialog, ToggleSwitch, TooltipModule, Editor, EditorSpellcheckDirective],
  templateUrl: './projectsetting.html',
  styleUrl: './projectsetting.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService, ConfirmationService]
})
export class ProjectsettingComponent implements OnInit, AfterViewInit, OnDestroy {
  projectId: number = 0;
  project: ProjectModel | null = null;
  canView: boolean = false;
  canEdit: boolean = false;
  canArchive: boolean = false;
  isProjectActive: boolean = true;
  isBackupInProgress: boolean = false;
  backupCompleted: boolean = false;
  backupFailed: boolean = false;
  backupProgress = { filesProcessed: 0, totalFiles: 0 };
  projectUsers: ProjectUserModel[] = [];
  selectedSiteManagerId: number | null = null;
  selectedFieldLeadId: number | null = null;
  siteContact: string = '';
  customerContactNumber: string = '';
  customerEmailAddress: string = '';
  managerNotes: string = '';
  warrantyInverterStartDate: string = '';
  warrantyInverterEndDate: string = '';
  warrantyTransformerStartDate: string = '';
  warrantyTransformerEndDate: string = '';
  warrantySkidStartDate: string = '';
  warrantySkidEndDate: string = '';
  contractPo: string = '';
  inverterTestReportPath: string = '';
  inverterTestReportSource: number | null = null;
  transformerTestReportPath: string = '';
  transformerTestReportSource: number | null = null;
  projectFolders: { fileFolderId: number; folderName: string }[] = [];
  isSyncingInverterReports: boolean = false;
  isSavingLeads: boolean = false;
  isPwaSite: boolean = false;

  backupFolderPath: string = '';
  backupPathRequired: boolean = false;

  showMapPopup: boolean = false;

  @ViewChild('settingsMap') mapContainer?: ElementRef<HTMLDivElement>;
  @ViewChild('popupMap') popupMapContainer?: ElementRef<HTMLDivElement>;

  private dataLoaded = false;
  private viewInitialized = false;
  private mapInitialized = false;
  private map: L.Map | null = null;
  private popupMap: L.Map | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private projectService: ProjectService,
    private projectStateService: ProjectStateService,
    private ssoService: SSOService,
    private permissionHelper: PermissionHelperService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private signalRService: SignalRService,
    private userService: UserService,
    private projectfileService: ProjectfileService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('projectId');
        if (id) {
          this.projectId = parseInt(id);
          this.tryLoadProject();
          // this.checkExistingBackupStatus(); // PHASE 2: Sync - revisit in next phase
          this.loadProjectLeads();
          this.loadProjectFolderNames();
          // this.loadBackupRootPath(); // PHASE 2: Sync - revisit in next phase
        }
      });

    this.projectStateService.currentProjectPermissions$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.canView = this.permissionHelper.has(PAGE_KEYS.SETTINGS, PERMISSION_KEYS.VIEW);
        this.canEdit = this.permissionHelper.has(PAGE_KEYS.SETTINGS, PERMISSION_KEYS.UPDATE);
        this.canArchive = this.permissionHelper.canCreateProject();
        this.tryLoadProject();
        this.cdr.markForCheck();
      });

    // PHASE 2: Sync - revisit in next phase
    // Subscribe to backup progress events from SignalR
    // this.signalRService.backupProgress$
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     filter(e => e !== null && e!.projectId === this.projectId)
    //   )
    //   .subscribe(event => {
    //     if (!event) return;
    //     this.isBackupInProgress = event.status === 'in_progress' || event.status === 'pending';
    //     this.backupCompleted = event.status === 'completed';
    //     this.backupFailed = event.status === 'failed';
    //     this.backupProgress = { filesProcessed: event.filesProcessed, totalFiles: event.totalFiles };
    //     this.cdr.markForCheck();
    //   });

    // PHASE 2: Sync - revisit in next phase
    // Subscribe to test report sync progress events from SignalR
    // this.signalRService.testReportSyncProgress$
    //   .pipe(
    //     takeUntil(this.destroy$),
    //     filter(e => e !== null && e!.projectId === this.projectId)
    //   )
    //   .subscribe(event => {
    //     if (!event) return;
    //     if (event.reportType === 'inverter') {
    //       this.isSyncingInverterReports = event.status === 'in_progress' || event.status === 'pending';
    //       if (event.status === 'completed') {
    //         this.messageService.add({ severity: 'success', summary: 'Sync Complete', detail: `Inverter reports synced: ${event.filesProcessed - event.skipped} uploaded, ${event.skipped} skipped.` });
    //       }
    //     }
    //     this.cdr.markForCheck();
    //   });
  }

  private tryLoadProject(): void {
    if (this.dataLoaded || !this.projectId || this.projectId <= 0) return;
    if (!this.canView) return;
    this.projectService.getProjectById(this.projectId).subscribe({
      next: (data) => {
        this.project = data;
        this.isProjectActive = !!data.isActive;
        if (data.backupFolderPath) {
          this.backupFolderPath = data.backupFolderPath;
        }
        if (data.siteManager) this.selectedSiteManagerId = data.siteManager;
        if (data.fieldLead) this.selectedFieldLeadId = data.fieldLead;
        this.siteContact = data.siteContact ?? '';
        this.customerContactNumber = data.customerContactNumber ?? '';
        this.customerEmailAddress = data.customerEmailAddress ?? '';
        this.managerNotes = data.managerNotes ?? '';
        this.warrantyInverterStartDate = data.warrantyInverterStartDate ? data.warrantyInverterStartDate.substring(0, 10) : '';
        this.warrantyInverterEndDate = data.warrantyInverterEndDate ? data.warrantyInverterEndDate.substring(0, 10) : '';
        this.warrantyTransformerStartDate = data.warrantyTransformerStartDate ? data.warrantyTransformerStartDate.substring(0, 10) : '';
        this.warrantyTransformerEndDate = data.warrantyTransformerEndDate ? data.warrantyTransformerEndDate.substring(0, 10) : '';
        this.warrantySkidStartDate = data.warrantySkidStartDate ? data.warrantySkidStartDate.substring(0, 10) : '';
        this.warrantySkidEndDate = data.warrantySkidEndDate ? data.warrantySkidEndDate.substring(0, 10) : '';
        this.contractPo = data.contractPo ?? '';
        this.inverterTestReportPath = data.inverterTestReportPath ?? '';
        this.inverterTestReportSource = data.inverterTestReportDestination ?? null;
        this.transformerTestReportPath = data.transformerTestReportPath ?? '';
        this.transformerTestReportSource = data.transformerTestReportDestination ?? null;
        this.isPwaSite = data.isPwaSite ?? false;
        this.dataLoaded = true;
        this.cdr.markForCheck();
        setTimeout(() => this.tryInitMap(), 0);
      },
      error: (err) => {
        console.error('Error loading project settings:', err);
      }
    });
  }

  private loadProjectLeads(): void {
    if (!this.projectId) return;
    this.userService.getProjectUserList(this.projectId).subscribe({
      next: (users) => {
        this.projectUsers = users;
        this.cdr.markForCheck();
      },
      error: () => { /* silently ignore */ }
    });
  }


  // PHASE 2: Sync inverter test reports - revisit in next phase
  // syncInverterReports(): void {
  //   if (!this.inverterTestReportPath?.trim()) {
  //     this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Please enter the inverter test report source path.' });
  //     return;
  //   }
  //   if (!this.inverterTestReportSource) {
  //     this.messageService.add({ severity: 'warn', summary: 'Required', detail: 'Please select an inverter report destination folder.' });
  //     return;
  //   }
  //   this.isSyncingInverterReports = true;
  //   this.cdr.markForCheck();
  //   this.projectfileService.syncTestReports(this.projectId, 'inverter', this.inverterTestReportPath.trim(), this.inverterTestReportSource).subscribe({
  //     next: () => {
  //       this.messageService.add({ severity: 'info', summary: 'Sync Started', detail: 'Inverter test report sync has been queued. You will be notified when complete.' });
  //       this.cdr.markForCheck();
  //     },
  //     error: () => {
  //       this.isSyncingInverterReports = false;
  //       this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to queue inverter test report sync.' });
  //       this.cdr.markForCheck();
  //     }
  //   });
  // }

  private loadProjectFolderNames(): void {
    if (!this.projectId) return;
    this.projectfileService.getProjectFolderNames(this.projectId).subscribe({
      next: (folders) => {
        this.projectFolders = folders;
        this.cdr.markForCheck();
      },
      error: () => { /* silently ignore */ }
    });
  }

  saveProjectLeads(): void {
    if (!this.canEdit) return;
    this.isSavingLeads = true;
    const request: UpdateProjectSettingsRequest = {
      siteManager: this.selectedSiteManagerId,
      fieldLead: this.selectedFieldLeadId,
      siteContact: this.siteContact || null,
      customerContactNumber: this.customerContactNumber || null,
      customerEmailAddress: this.customerEmailAddress || null,
      managerNotes: this.managerNotes || null,
      warrantyInverterStartDate: this.warrantyInverterStartDate || null,
      warrantyInverterEndDate: this.warrantyInverterEndDate || null,
      warrantyTransformerStartDate: this.warrantyTransformerStartDate || null,
      warrantyTransformerEndDate: this.warrantyTransformerEndDate || null,
      warrantySkidStartDate: this.warrantySkidStartDate || null,
      warrantySkidEndDate: this.warrantySkidEndDate || null,
      contractPo: this.contractPo || null,
      inverterTestReportPath: this.inverterTestReportPath || null,
      inverterTestReportDestination: this.inverterTestReportSource,
      transformerTestReportPath: this.transformerTestReportPath || null,
      transformerTestReportDestination: this.transformerTestReportSource,
      backupFolderPath: this.backupFolderPath || null,
      isPwaSite: this.isPwaSite
    };
    this.projectService.updateProjectLeads(this.projectId, request).subscribe({
      next: () => {
        this.isSavingLeads = false;
        if (this.project) {
          this.project = { ...this.project, siteManager: this.selectedSiteManagerId ?? undefined, fieldLead: this.selectedFieldLeadId ?? undefined, siteContact: this.siteContact || undefined, customerContactNumber: this.customerContactNumber || undefined, customerEmailAddress: this.customerEmailAddress || undefined, managerNotes: this.managerNotes || undefined };
        }
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Project leads updated.' });
        this.cdr.markForCheck();
      },
      error: () => {
        this.isSavingLeads = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save project leads.' });
        this.cdr.markForCheck();
      }
    });
  }

  private checkExistingBackupStatus(): void {
    if (!this.projectId) return;
    this.projectService.getBackupStatus(this.projectId).subscribe({
      next: (status: any) => {
        if (status?.status === 'in_progress' || status?.status === 'pending') {
          this.isBackupInProgress = true;
          this.backupProgress = { filesProcessed: status.filesProcessed ?? 0, totalFiles: status.totalFiles ?? 0 };
          this.cdr.markForCheck();
        } else if (status?.status === 'completed') {
          this.backupCompleted = true;
          this.cdr.markForCheck();
        } else if (status?.status === 'failed') {
          this.backupFailed = true;
          this.isBackupInProgress = false;
          this.cdr.markForCheck();
        }
      },
      error: () => { /* silently ignore */ }
    });
  }

  get currentUserId(): number | undefined {
    return this.ssoService.getCurrentUser()?.userId;
  }

  get archiveLabel(): string {
    return this.project?.isActive ? 'Archive Project' : 'Unarchive Project';
  }

  private calcMonths(start: string, end: string): number | null {
    if (!start || !end) return null;
    const s = new Date(start), e = new Date(end);
    if (isNaN(s.getTime()) || isNaN(e.getTime()) || e <= s) return null;
    return (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
  }

  get inverterDurationMonths(): number | null { return this.calcMonths(this.warrantyInverterStartDate, this.warrantyInverterEndDate); }
  get transformerDurationMonths(): number | null { return this.calcMonths(this.warrantyTransformerStartDate, this.warrantyTransformerEndDate); }
  get skidDurationMonths(): number | null { return this.calcMonths(this.warrantySkidStartDate, this.warrantySkidEndDate); }

  onStatusToggle(event: any): void {
    const switchingToInactive = !event.checked;

    if (switchingToInactive) {
      // if (this.backupFolderPath.trim()) {
      //   this.callArchiveProject(this.backupFolderPath.trim());
      // } else {
      //   this.confirmationService.confirm({
      //     message: 'No backup path provided. Do you want to make the project inactive without taking a backup?',
      //     header: 'No Backup Path',
      //     acceptLabel: 'Yes, make inactive',
      //     rejectLabel: 'No',
      //     accept: () => this.callArchiveProject(),
      //     reject: () => {
      //       this.isProjectActive = true;
      //       this.cdr.markForCheck();
      //     }
      //   });
      // }
      this.callArchiveProject();
    } else {
      this.callArchiveProject();
    }
  }

  // private loadBackupRootPath(): void {
  //   this.projectService.getBackupRootPath().subscribe({
  //     next: (res) => {
  //       if (!this.backupFolderPath) {
  //         this.backupFolderPath = res.rootPath;
  //         this.cdr.markForCheck();
  //       }
  //     },
  //     error: () => {}
  //   });
  // }

  // onResync(): void {
  //   if (!this.backupFolderPath.trim()) {
  //     this.backupPathRequired = true;
  //     this.cdr.markForCheck();
  //     return;
  //   }
  //   this.projectService.resyncBackup(this.projectId, this.backupFolderPath.trim()).subscribe({
  //     next: () => {
  //       this.isBackupInProgress = true;
  //       this.messageService.add({
  //         severity: 'info',
  //         summary: 'Resync Started',
  //         detail: 'Backup resync is running in the background.'
  //       });
  //       this.cdr.markForCheck();
  //     },
  //     error: () => {
  //       this.messageService.add({
  //         severity: 'error',
  //         summary: 'Error',
  //         detail: 'Failed to start resync. Please try again.'
  //       });
  //       this.cdr.markForCheck();
  //     }
  //   });
  // }

  private callArchiveProject(folderPath?: string): void {
    this.projectService.archiveProject(this.projectId, folderPath).subscribe({
      next: () => {
        if (this.project) {
          this.project = { ...this.project, isActive: this.isProjectActive };
        }
        // if (!this.isProjectActive) {
        //   this.isBackupInProgress = true;
        //   this.messageService.add({
        //     severity: 'info',
        //     summary: 'Backup Started',
        //     detail: 'Project archived. Backup is running in the background.'
        //   });
        // }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Error toggling project status:', err);
        this.isProjectActive = !this.isProjectActive;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to update project status. Please try again.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  leaveProject(): void {
    const userId = this.currentUserId;
    if (!userId) return;
    if (!confirm('Are you sure you want to leave this project? You will lose access to it.')) return;

    this.projectService.deleteProjectUser(this.projectId, userId).subscribe({
      next: () => {
        this.router.navigate(['/projects']);
      },
      error: (err) => {
        console.error('Error leaving project:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to leave project. Please try again.'
        });
        this.cdr.markForCheck();
      }
    });
  }

  archiveProject(): void {
    const action = this.project?.isActive ? 'archive' : 'unarchive';
    if (!confirm(`Are you sure you want to ${action} this project?`)) return;

    this.projectService.archiveProject(this.projectId).subscribe({
      next: () => {
        this.router.navigate(['/projects']);
      },
      error: (err) => {
        console.error(`Error ${action}ing project:`, err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: `Failed to ${action} project. Please try again.`
        });
        this.cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    this.tryInitMap();
  }

  private tryInitMap(): void {
    if (this.mapInitialized || !this.viewInitialized || !this.dataLoaded) return;
    if (!this.mapContainer?.nativeElement) return;

    const lat = parseFloat(this.project?.latitude ?? '');
    const lng = parseFloat(this.project?.longitude ?? '');
    if (isNaN(lat) || isNaN(lng)) return;

    this.initMap(lat, lng);
  }

  private initMap(lat: number, lng: number): void {
    const el = this.mapContainer!.nativeElement;

    this.map = L.map(el, { center: [lat, lng], zoom: 10 });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19
    }).addTo(this.map);

    const popup = `
      <div style="min-width:160px">
        <strong>${this.project!.projectName}</strong><br/>
        <span style="color:#666">#${this.project!.projectNumber}</span>
        ${this.project!.customerAddress1 ? `<br/><span>${this.project!.customerAddress1}</span>` : ''}
        ${this.project!.customerCity ? `<br/><span>${this.project!.customerCity}${this.project!.customerState ? ', ' + this.project!.customerState : ''}</span>` : ''}
      </div>`;

    L.marker([lat, lng], { icon: psIconDefault })
      .addTo(this.map)
      .bindPopup(popup)
      .openPopup();

    this.resizeObserver = new ResizeObserver(() => this.map?.invalidateSize());
    this.resizeObserver.observe(el);

    requestAnimationFrame(() => this.map?.invalidateSize());

    this.mapInitialized = true;
  }

  openMapPopup(): void {
    this.showMapPopup = true;
    this.cdr.markForCheck();

    setTimeout(() => {
      const el = this.popupMapContainer?.nativeElement;
      if (!el) return;

      const lat = parseFloat(this.project?.latitude ?? '');
      const lng = parseFloat(this.project?.longitude ?? '');
      if (isNaN(lat) || isNaN(lng)) return;

      this.popupMap = L.map(el, { center: [lat, lng], zoom: 12 });

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(this.popupMap);

      const popup = `
        <div style="min-width:160px">
          <strong>${this.project!.projectName}</strong><br/>
          <span style="color:#666">#${this.project!.projectNumber}</span>
          ${this.project!.customerAddress1 ? `<br/><span>${this.project!.customerAddress1}</span>` : ''}
          ${this.project!.customerCity ? `<br/><span>${this.project!.customerCity}${this.project!.customerState ? ', ' + this.project!.customerState : ''}</span>` : ''}
        </div>`;

      L.marker([lat, lng], { icon: psIconDefault })
        .addTo(this.popupMap)
        .bindPopup(popup)
        .openPopup();

      requestAnimationFrame(() => this.popupMap?.invalidateSize());
    }, 0);
  }

  closeMapPopup(): void {
    if (this.popupMap) {
      this.popupMap.remove();
      this.popupMap = null;
    }
    this.showMapPopup = false;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.resizeObserver?.disconnect();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
    if (this.popupMap) {
      this.popupMap.remove();
      this.popupMap = null;
    }
  }
}
